import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';

interface NumericPinInputProps {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  label: string;
  visible?: boolean;
}

const PIN_LENGTH = 6;

export default function NumericPinInput({ value, onChange, autoFocus = false, label, visible = false }: NumericPinInputProps) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: PIN_LENGTH }, (_, index) => value[index] || '');

  useEffect(() => {
    if (autoFocus) inputs.current[0]?.focus();
  }, [autoFocus]);

  const updateDigit = (index: number, rawValue: string) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    if (!digit && rawValue) return;
    if (!digit) {
      onChange(value.slice(0, index));
      return;
    }
    const nextDigits = [...digits];
    nextDigits[index] = digit;
    onChange(nextDigits.join('').slice(0, PIN_LENGTH));
    if (digit && index < PIN_LENGTH - 1) {
      window.setTimeout(() => inputs.current[index + 1]?.focus(), 0);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    inputs.current[Math.min(pasted.length, PIN_LENGTH) - 1]?.focus();
  };

  return <PinGroup aria-label={label}>
    {digits.map((digit, index) => <PinBox
      key={index}
      ref={element => { inputs.current[index] = element; }}
      type={visible ? 'text' : 'password'}
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={1}
      autoComplete="off"
      aria-label={`${label}第 ${index + 1} 位`}
      value={digit}
      onChange={event => updateDigit(index, event.target.value)}
      onPaste={handlePaste}
      onFocus={event => {
        const firstEmpty = digits.findIndex(item => !item);
        if (firstEmpty >= 0 && firstEmpty < index) {
          inputs.current[firstEmpty]?.focus();
          return;
        }
        event.currentTarget.select();
      }}
      onKeyDown={event => {
        if (event.key === 'Backspace' && !digit && index > 0) inputs.current[index - 1]?.focus();
        if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
        if (event.key === 'ArrowRight' && index < PIN_LENGTH - 1) inputs.current[index + 1]?.focus();
      }}
    />)}
  </PinGroup>;
}

const PinGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const PinBox = styled.input`
  width: 48px;
  height: 52px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  text-align: center;
  font-size: 24px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #ff671d;
    box-shadow: 0 0 0 2px rgba(255, 103, 29, 0.15);
  }
`;
